import React, { useState } from 'react';
import {
    Box,
    Title,
    Text,
    Paper,
    Select,
    Textarea,
    Button,
    Alert,
    Badge,
    SimpleGrid,
    Stack,
    Group,
} from '@mantine/core';
import { useEntityTypes } from '../../contexts/EntityTypesContext';
import { getScimConfig } from '../../utils/envUtils';
import { getAxiosInstance } from '../../services/axiosInstance';
import { LoadingState } from '../common/LoadingState';
import { debugLog } from '../../utils/debugLog';

interface ScimSelectProps {
    authResolved: boolean;
}

const ScimSelect: React.FC<ScimSelectProps> = ({ authResolved }) => {
    const { allEntityTypes } = useEntityTypes();

    const [selectedEntityType, setSelectedEntityType] = useState<string>('');
    const [scimSyntax, setScimSyntax] = useState<string>('');
    const [result, setResult] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const blacklistedTypes = [
        'ResourceTypes',
        'Service Provider Configuration',
        'Resource Type'
    ];

    const availableEntityTypes = allEntityTypes.length > 0
        ? [...allEntityTypes]
            .filter(type => !blacklistedTypes.includes(type))
            .sort((a, b) => a.localeCompare(b))
        : ['Person'];

    const handleEntityTypeChange = (value: string | null) => {
        setSelectedEntityType(value || '');
        setScimSyntax('');
        setResult(null);
        setError(null);
    };

    const handleScimSyntaxChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setScimSyntax(event.currentTarget.value);
        setResult(null);
        setError(null);
    };

    const handleTest = async () => {
        if (!selectedEntityType) {
            setError('Please select an entity type');
            return;
        }

        setLoading(true);
        setError(null);
        setResult(null);

        try {
            debugLog(`🔍 ScimSelect: Testing ${selectedEntityType} with select: "${scimSyntax || '(empty)'}"`);

            const scimConfig = getScimConfig();
            const api = getAxiosInstance(scimConfig.apiUrl);
            const endpoint = `/entities/${selectedEntityType}`;
            const params: any = {
                startIndex: 1,
                count: 10,
                skipReferenceExpansion: true
            };
            if (scimSyntax.trim()) {
                params.attributes = scimSyntax.trim();
            }

            debugLog(`🌐 ScimSelect: Making API call to ${scimConfig.apiUrl}${endpoint}`, params);

            const response = await api.get(endpoint, { params });

            debugLog(`📊 ScimSelect: Received response:`, response.data);

            const responseData = response.data;
            const resources = responseData.Resources || [];
            const totalResults = responseData.totalResults || 0;

            setResult({
                entityType: selectedEntityType,
                scimSyntax: scimSyntax || '(empty - all data)',
                isValid: true,
                message: scimSyntax.trim()
                    ? `SCIM syntax is valid - returned ${resources.length} of ${totalResults} total results`
                    : `No select syntax provided - returned all data (${resources.length} of ${totalResults} total results)`,
                sampleData: {
                    totalResults: totalResults,
                    itemsPerPage: responseData.itemsPerPage || 10,
                    startIndex: responseData.startIndex || 1,
                    Resources: resources
                }
            });

        } catch (err: any) {
            console.error('❌ ScimSelect: API call failed:', err);

            let errorMessage = 'Unknown error occurred';
            if (err.response) {
                errorMessage = `Server error (${err.response.status}): ${err.response.data?.detail || err.response.statusText}`;
            } else if (err.request) {
                errorMessage = 'Network error: No response from server';
            } else {
                errorMessage = `Request error: ${err.message}`;
            }

            setError(`Error testing SCIM select: ${errorMessage}`);
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        setScimSyntax('');
        setResult(null);
        setError(null);
    };

    if (!authResolved) {
        return (
            <LoadingState variant="centered" />
        );
    }

    return (
        <Box p="lg">
            <Title order={1} mb="xs">
                SCIM Select Tester
            </Title>

            <Text size="sm" c="dimmed" mb="lg">
                Test SCIM select syntax against different entity types to validate queries and explore data structures.
            </Text>

            <SimpleGrid cols={{ base: 1, md: 2 }}>
                {/* Configuration */}
                <Paper withBorder p="md">
                    <Title order={5} mb="md">
                        Configuration
                    </Title>

                    <Select
                        label="Entity Type"
                        value={selectedEntityType}
                        onChange={handleEntityTypeChange}
                        data={availableEntityTypes.map((type) => ({ value: type, label: type }))}
                        mb="md"
                    />

                    <Textarea
                        label="SCIM Select Syntax (Optional)"
                        placeholder="Enter SCIM select syntax (e.g., id,userName,emails.value,addresses[type eq 'work'].streetAddress) or leave empty to get all data"
                        value={scimSyntax}
                        onChange={handleScimSyntaxChange}
                        minRows={4}
                        styles={{
                            input: {
                                fontFamily: 'monospace',
                                fontSize: '0.875rem'
                            }
                        }}
                    />

                    <Group gap="xs" mt="md">
                        <Button
                            onClick={handleTest}
                            loading={loading}
                            disabled={!selectedEntityType}
                        >
                            {loading ? 'Testing...' : 'Test Query'}
                        </Button>

                        <Button
                            variant="outline"
                            onClick={handleClear}
                            disabled={loading}
                        >
                            Clear
                        </Button>
                    </Group>
                </Paper>

                {/* Results */}
                <Paper withBorder p="md">
                    <Title order={5} mb="md">
                        Results
                    </Title>

                    {error && (
                        <Alert color="red" mb="md">
                            {error}
                        </Alert>
                    )}

                    {result && (
                        <Stack gap="md">
                            <Group gap="xs" wrap="wrap">
                                <Badge variant="outline" color="blue">
                                    Entity: {result.entityType}
                                </Badge>
                                <Badge variant="outline" color={result.isValid ? 'green' : 'red'}>
                                    {result.isValid ? 'Valid' : 'Invalid'}
                                </Badge>
                            </Group>

                            <Text size="sm" c="dimmed">
                                {result.message}
                            </Text>

                            <Paper p="md" withBorder>
                                <Text size="sm" fw={500} mb="xs">
                                    Sample Response:
                                </Text>
                                <Box component="pre" ff="monospace" fz="sm" m={0} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {JSON.stringify(result.sampleData, null, 2)}
                                </Box>
                            </Paper>
                        </Stack>
                    )}

                    {!result && !error && (
                        <Text size="sm" c="dimmed">
                            Select an entity type to test. SCIM syntax is optional - leave empty to get all data.
                        </Text>
                    )}
                </Paper>
            </SimpleGrid>

            {/* Help Section */}
            <Paper withBorder p="md" mt="lg">
                <Title order={5} mb="md">
                    SCIM Select Syntax Help
                </Title>
                <Text size="sm" c="dimmed" mb="xs">
                    SCIM select syntax allows you to specify which attributes to return in a query response.
                </Text>
                <Text size="sm" c="dimmed" mb="xs">
                    <strong>Examples:</strong>
                </Text>
                <Box component="pre" ff="monospace" fz="sm" p="md" bg="var(--mantine-color-default-hover)" style={{ borderRadius: 4, overflow: 'auto', border: '1px solid var(--mantine-color-default-border)' }}>
                    {`• Basic attributes: id,userName,displayName
• Nested attributes: name.givenName,name.familyName
• Array elements: emails.value,addresses.streetAddress
• Filtered arrays: addresses[type eq 'work'].streetAddress
• Complex queries: name.givenName,emails[primary eq true].value`}
                </Box>
            </Paper>
        </Box>
    );
};

export default ScimSelect;
